import { memo } from 'react';
import {
  LockKeyhole,
  Signal,
  CirclePlay,
  Radio,
  Trophy,
  Film,
  LayoutGrid,
  Server,
  MonitorSmartphone,
  RefreshCw,
  ShieldCheck,
  Monitor,
  Check,
} from 'lucide-react';

import Google from '../assets/logo/Google_Pay.png';
import cb from '../assets/logo/GIE_CB.png';
import visa from '../assets/logo/Visa.png';
import apple from '../assets/logo/Apple_Pay.png';
import discover from '../assets/logo/Discover.png';
import mastercard from '../assets/logo/Mastercard.png';

const ICON_SIZE = 20;
const ICON_STROKE_WIDTH = 2;

// €39 and €69 plans: icons always yellow (no hover change). Others: blue default, yellow on hover.
const ICONS_ALWAYS_YELLOW_PLANS = ['premium', 'multi-3s'];
const iconCls = (isPopular, iconsAlwaysYellow) => {
  if (iconsAlwaysYellow) return 'text-yellow-400 flex-shrink-0';
  return isPopular
    ? 'text-blue-400 group-hover:text-yellow-400 transition-colors duration-300 ease-in-out flex-shrink-0'
    : 'text-blue-600 group-hover:text-yellow-500 transition-colors duration-300 ease-in-out flex-shrink-0';
};

const getFeatureIcon = (featureText, isPopular, iconsAlwaysYellow) => {
  const text = featureText.toLowerCase();
  const iconProps = {
    size: ICON_SIZE,
    strokeWidth: ICON_STROKE_WIDTH,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    className: iconCls(isPopular, iconsAlwaysYellow),
  };

  // Adult content → Lock with keyhole (unique)
  if (text.includes('adultos') && (text.includes('palavra') || text.includes('contraseña'))) return <LockKeyhole {...iconProps} />;
  // +79.000 canais +249.000 → Broadcast/Signal (unique)
  if (
    (text.includes('79.000') || text.includes('79,000')) &&
    (text.includes('249.000') || text.includes('249,000'))
  )
    return <Signal {...iconProps} />;
  // Amazon Prime, Netflix, etc. → Play circle (any match; single line lists several names)
  if (
    text.includes('amazon') ||
    text.includes('netflix') ||
    text.includes('hbo') ||
    text.includes('disney') ||
    text.includes('apple tv') ||
    text.includes('discovery') ||
    text.includes('viaplay')
  )
    return <CirclePlay {...iconProps} />;
  // Fórmula 1 → Trophy before Sport TV (both lines mention Sport TV)
  if (text.includes('fórmula') || text.includes('formula') || text.includes('f1')) return <Trophy {...iconProps} />;
  // Assistir Sport TV em direto → Live/Radio (unique vs linha F1)
  if (text.includes('assistir') && text.includes('sport tv')) return <Radio {...iconProps} />;
  // Escolha ilimitada → Film reel (unique)
  if (text.includes('ilimitada')) return <Film {...iconProps} />;
  // Qualidade SD/HD/4K/8K → LayoutGrid (unique)
  if (text.includes('qualidade')) return <LayoutGrid {...iconProps} />;
  // Servidores → Server rack (unique)
  if ((text.includes('rápido') || text.includes('rápidos')) && text.includes('servidor')) return <Server {...iconProps} />;
  // Dispositivos → TV+Mobile (unique)
  if (text.includes('dispositivos')) return <MonitorSmartphone {...iconProps} />;
  // Atualizações regulares → Refresh/Sync (unique)
  if (text.includes('atualizações') || text.includes('atualizacoes')) return <RefreshCw {...iconProps} />;
  // Garantia → Shield with check (unique)
  if (text.includes('garantia') || text.includes('garantía')) return <ShieldCheck {...iconProps} />;
  return <Check {...iconProps} />;
};

const IBOProBadge = () => (
  <div className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400 py-2 rounded-lg mb-6 flex items-center justify-center gap-2">
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 32 32">
      <g fill="none">
        <path fill="#fcd53f" d="M24.888 30H7.112A2.093 2.093 0 0 1 5 27.914V15l10.877-3.06L27 15v12.914C27 29.06 26.06 30 24.888 30" />
        <path fill="#f8312f" d="M27 5.847c0-3.384-4.045-5.106-6.466-2.763L16 7.479l-4.533-4.395C9.045.73 5 2.463 5 5.847C5 7.04 5.957 8 7.142 8L16 10.17L24.858 8A2.157 2.157 0 0 0 27 5.847" />
        <path fill="#ffb02e" d="M27.662 8H4.337C3.6 8 3 8.6 3 9.342v4.316C3 14.4 3.6 15 4.338 15h23.324C28.4 15 29 14.4 29 13.658V9.342C29 8.6 28.4 8 27.662 8" />
        <path fill="#ca0b4a" d="M16.005 3C14.345 3 13 4.37 13 6.06V30h6V6.06C19.01 4.37 17.666 3 16.005 3" />
      </g>
    </svg>
    <span className="font-semibold">+ Lector IBO Pro gratis!</span>
  </div>
);

const paymentLogos = [
  { src: Google, alt: 'Google Pay' },
  { src: cb, alt: 'CB' },
  { src: visa, alt: 'Visa' },
  { src: apple, alt: 'Apple Pay' },
  { src: discover, alt: 'Discover' },
  { src: mastercard, alt: 'Mastercard' },
];

function PricingCardComponent({ plan, createCheckoutUrl, planId }) {
  const {
    title,
    price,
    period,
    badge,
    screens,
    isPopular,
    link,
    features,
  } = plan;

  const iconsAlwaysYellow = ICONS_ALWAYS_YELLOW_PLANS.includes(planId);

  const baseClasses = 'service-card rounded-2xl shadow-lg p-6 h-full border border-gray-100 transition-all duration-300 overflow-hidden transform hover:-translate-y-1 hover:border-gray-200';
  const popularClasses = 'bg-gradient-to-br from-[#0a1a5f] via-[#002f9c] to-[#0d47ff] pricing-card-gold group';
  const regularClasses = 'bg-white hover-gold pricing-card-basic group';
  const cardClasses = `${baseClasses} ${isPopular ? popularClasses : regularClasses}`;

  const textColor = isPopular ? 'text-[#E8E8E8]' : 'text-gray-700';

  const handleOrder = () => {
    window.open(createCheckoutUrl(planId, link), '_blank');
  };

  return (
    <div className={`relative ${cardClasses}`}>
      {badge && (
        <div className="absolute top-10 right-[-40px] rotate-45 z-10">
          <div className={`text-xs font-bold uppercase tracking-wide px-14 py-1 shadow-sm ${
            isPopular
              ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-[#002f9c]'
              : 'bg-gradient-to-r from-[#0a1a5f] via-[#002f9c] to-[#0d47ff] text-white badge-silver'
          }`}>
            {badge}
          </div>
        </div>
      )}

      <div className="text-left mb-6 relative z-0">
        <h3 className={`text-2xl font-bold mb-2 ${isPopular ? 'text-[#FAFAFA]' : 'text-blue-900'}`}>
          {title}
        </h3>
        <div className="text-xl mt-4">
          <span className={`line-through ${isPopular ? 'text-[#E8E8E8]' : 'text-gray-700'}`}>{plan.originalPrice}</span>
        </div>
        <div className={`text-4xl font-bold mb-1 ${
          isPopular
            ? 'text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400'
            : 'text-transparent bg-clip-text bg-gradient-to-r from-[#0a1a5f] via-[#002f9c] to-[#0d47ff] pricing-text-basic'
        }`}>
          {price}
        </div>
        <div className={`text-sm mb-3 ${isPopular ? 'text-[#E8E8E8]' : 'text-gray-600'}`}>
          / {period}
        </div>

        <IBOProBadge />

        {screens > 0 && (
          <div className={`py-2 rounded-lg mb-6 flex items-center justify-start gap-3 ${isPopular ? 'text-[#E8E8E8]' : 'text-gray-700'}`}>
            <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
              <Monitor size={ICON_SIZE} strokeWidth={ICON_STROKE_WIDTH} strokeLinecap="round" strokeLinejoin="round" className={iconCls(isPopular, iconsAlwaysYellow)} aria-hidden />
            </div>
            <span className="font-semibold text-sm">
              {screens} {screens === 1 ? 'pantalla activa' : 'pantallas activas'}
            </span>
          </div>
        )}
      </div>

      <div className="space-y-3 mb-6">
        {features.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">{getFeatureIcon(feature, isPopular, iconsAlwaysYellow)}</div>
            <span className={`text-sm ${textColor} leading-relaxed`}>{feature}</span>
          </div>
        ))}
      </div>

      <button
        onClick={handleOrder}
        type="button"
        className={`w-full py-3 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl transform hover:scale-105 ${
          isPopular
            ? 'bg-white hover:bg-gray-100 text-gray-900'
            : 'bg-gradient-to-r from-[#0a1a5f] via-[#002f9c] to-[#0d47ff] hover:from-[#002f9c] hover:to-[#0d47ff] text-white'
        }`}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
        </svg>
        <span>ORDENAR AHORA</span>
      </button>

      <div className="text-center mb-3 mt-6">
        <div className={`flex items-center justify-center gap-2 text-xs ${isPopular ? 'text-white' : 'text-gray-600'}`}>
          <ShieldCheck className={`w-4 h-4 flex-shrink-0 ${isPopular ? 'text-yellow-400' : 'text-blue-600'}`} strokeWidth={2} aria-hidden />
          <span>Pago 100% Seguro</span>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mt-4 p-3 bg-white rounded-xl w-full">
          {paymentLogos.map(({ src, alt }) => (
            <img key={alt} src={src} alt={alt} className="footer-logo w-10 h-auto drop-shadow-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

export const PricingCard = memo(PricingCardComponent);
